import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { setFilters } from 'actions/search';
import { getActiveFilters } from 'selectors/search';
import { journals } from 'services/inputTokens';

import { Filter } from '../helpers';
import FilterGroup from './FilterGroup';
import { advancedKeys, advancedResearcerKeys } from './keys';
import { tagRender } from 'components/common/TagRender';
import { renderOption } from 'components/common/OptionRender';
import { getPresetFilterDefaults } from './presetSideEffects';

const ImpactScoreHelp = () => {
  const handleOpenFaq = () => {
    const { fcWidget } = window;
    if (fcWidget) {
      fcWidget.open();
    }
  };
  return (
    <div>
      Research Impact relates to H-Index. For more help visit our{' '}
      <span
        onClick={handleOpenFaq}
        style={{ cursor: 'pointer', color: 'rgb(24, 144, 255)' }}
      >
        FAQ
      </span>
    </div>
  );
};

const Advanced = ({ loading }) => {
  const dispatch = useDispatch();

  const keys = [
    ...Object.keys(advancedKeys),
    ...Object.keys(advancedResearcerKeys),
  ];
  const activeFilters = useSelector(getActiveFilters(keys));

  const handleKolGroup = (key, value) => {
    dispatch(
      setFilters({
        kol: false,
        [key]: value,
      })
    );
  };

  const handleKol = value => {
    if (value) {
      dispatch(
        setFilters({
          kol: true,
          ...getPresetFilterDefaults({ kol: true }),
        })
      );
    } else {
      dispatch(
        setFilters({
          kol: false,
          threeYearHIndexAverage: 0,
          totalPublications: 0,
        })
      );
    }
  };

  return (
    <FilterGroup
      icon='settings'
      title='Advanced / KOL'
      activeKeys={activeFilters}
      sectionKey='advanced'
    >
      <>
        <Filter
          filterType='switch'
          filterId='kol'
          onChange={handleKol}
          disabled={loading}
        >
          Key Opinion Leaders Only
        </Filter>
        <Filter
          filterType='selectIncludeExclude'
          mode={'multiple'}
          style={{ width: '100%' }}
          renderOption={renderOption}
          tagRender={tagRender}
          filterId={advancedKeys.journal.key}
          placeholder='Journals or Preprint Repository'
          helpText='Filter results by journals or preprint repositories relating to your search'
          options={journals}
          minInputSize={3}
          disabled={loading}
        />
        <Filter
          filterType='number'
          filterId={advancedKeys.totalPublications.key}
          placeholder='Min. Publications'
          min={0}
          onChange={value => handleKolGroup('totalPublications', value)}
          disabled={loading}
        />
        <Filter
          filterType='number'
          filterId={advancedKeys.totalCollaboratorsOverall.key}
          placeholder='Min. Collaborators'
          min={0}
          disabled={loading}
        />
        <Filter
          filterType='number'
          filterId={advancedKeys.totalFirstNamedAuthorCount.key}
          placeholder='First Author Positions'
          data-testid='first-author'
          min={0}
          disabled={loading}
        />
        <Filter
          filterType='number'
          filterId={advancedKeys.totalLastNamedAuthorCount.key}
          placeholder='Last Author Positions'
          data-testid='last-author'
          min={0}
          disabled={loading}
        />
        <Filter
          filterType='number'
          filterId={advancedKeys.totalGrants.key}
          placeholder='Min. Total Funding Awards'
          min={0}
          disabled={loading}
        />
        <Filter
          filterType='number'
          filterId={advancedKeys.threeYearHIndexAverage.key}
          min={0}
          placeholder='Min. Research Impact (3 Year Avg)'
          helpText={<ImpactScoreHelp />}
          onChange={value => handleKolGroup('threeYearHIndexAverage', value)}
          disabled={loading}
        />
        <Filter
          filterType='checkbox'
          filterId={advancedKeys.firstAuthor.key}
          filterGroup='namedAuthor'
          disabled={loading}
          labelText='First Named Author'
        />
        <Filter
          filterType='checkbox'
          filterId={advancedKeys.middleAuthor.key}
          filterGroup='namedAuthor'
          disabled={loading}
          labelText='Middle Named Author'
        />
        <Filter
          filterType='checkbox'
          filterId={advancedKeys.lastAuthor.key}
          filterGroup='namedAuthor'
          disabled={loading}
          labelText='Last Named Author'
        />
        <Filter
          filterType='checkbox'
          filterId={advancedResearcerKeys.validEmails.key}
          disabled={loading}
          labelText={advancedResearcerKeys.validEmails.description}
        />
        <Filter
          filterType='checkbox'
          filterId={advancedResearcerKeys.excludeRecentlyExportedEmails.key}
          helpText={advancedResearcerKeys.excludeRecentlyExportedEmails.tooltip}
          disabled={loading}
          labelText={
            advancedResearcerKeys.excludeRecentlyExportedEmails.description
          }
        />
      </>
    </FilterGroup>
  );
};

Advanced.propTypes = {
  loading: PropTypes.bool,
};

export default Advanced;
